import React, { useContext } from 'react'
import invariant from 'tiny-invariant'

import CruiseV2SavedPriceDetails from './CruiseV2SavedPriceDetails'
import PlannedCruiseCtaContent from './PlannedCruiseCtaContent'
import { ItemCardVariant } from '../ItemCard'
import ItineraryItemCard from '../ItineraryItemCard'

import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import CruiseV2OfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/CruiseV2OfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import { CruiseItem, LETripItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatDatesAndDays,
  formatStartAndEndLocation,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'

interface Props {
  item: Extract<LETripItem, CruiseItem>
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

export default function CruiseV2ItemCard({
  item,
  onClickCard,
  onClickTripName,
  variant,
}: Props) {
  const timeFormat = useRegionTimeFormat()

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit ?? false

  invariant(
    item.savedItemData.offerType === 'cruise',
    'CruiseV2ItemCard can only be used for cruise V2 offer',
  )

  const timeDescription: string | undefined = formatDatesAndDays(
    item,
    timeFormat,
  )

  if (item.isBooked) {
    const myEscapesLink =
      canEdit && !viewAsTemplate ? buildMyEscapesOrderLink(item) : undefined
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription]}
        onClickTripName={onClickTripName}
        variant={variant}
        onClick={myEscapesLink ? () => onClickCard(myEscapesLink) : undefined}
      />
    )
  }

  const offerLink: string = buildProductLink(item) ?? ''
  const {
    priceDisplayType,
    price,
    currencyCode,
    cabinCategory,
    imageId,
    isOfferSoldOut,
  } = item.savedItemData
  const isOfferAvailable = !isOfferSoldOut && !!price
  let priceAreaContent: JSX.Element | null = null

  if (viewAsTemplate) {
    priceAreaContent = (
      <CruiseV2OfferPriceDetails offerId={item.savedItemData.offerId} />
    )
  } else {
    priceAreaContent = (
      <CruiseV2SavedPriceDetails
        price={price}
        currencyCode={currencyCode}
        priceDisplayType={priceDisplayType}
        cabinCategory={cabinCategory}
        isOfferAvailable={isOfferAvailable}
      />
    )
  }

  const ctaAreaContent = (
    <PlannedCruiseCtaContent
      item={item}
      isOfferAvailable={isOfferAvailable}
      variant={variant}
    />
  )

  const locationDescription = (
    <BookmarkLocation
      variant="single"
      startLocation={item.startPlace.name}
      showIcon={false}
    />
  )

  return (
    <SavedOfferCard
      item={item}
      name={item.name}
      imgId={imageId}
      locationDescription={locationDescription}
      descriptors={[timeDescription]}
      priceAreaContent={priceAreaContent}
      ctaAreaContent={ctaAreaContent}
      testId="saved-cruise-card"
      onClick={() => onClickCard(offerLink)}
      offerLink={offerLink}
      onClickTripName={onClickTripName}
      variant={variant}
      isSoldOut={!isOfferAvailable}
    />
  )
}
